import {
    ComputeBudgetProgram,
    Connection,
    Keypair,
    PublicKey,
    SystemProgram,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { useJito } from './useJito';
import bs58 from 'bs58';
import { transferSolPriorityFee, transferSolPriorityLimit } from '../../ConfigFees';

export const useSolana = () => {
    const { sendBundle, spamAndConfirmBundle } = useJito();

    async function getMultipleSolBalances(connection: Connection, publicKeys: PublicKey[]): Promise<number[]> {
        //slice for groups of 100 public keys
        const balances: number[] = [];
        for (let i = 0; i < publicKeys.length; i += 100) {
            const localPublicKeys = publicKeys.slice(i, i + 100);
            const accountInfos = await connection.getMultipleAccountsInfo(localPublicKeys, 'processed');
            for (const accountInfo of accountInfos) {
                if (accountInfo === null) {
                    balances.push(0);
                    continue;
                }
                const lamports = accountInfo.lamports;
                if (lamports === undefined || lamports === undefined) {
                    balances.push(0);
                } else balances.push(lamports);
            }
        }
        return balances;
    }

    function decodeBase58PrivateKey(privateKey: string) {
        return bs58.decode(privateKey);
    }

    function getTransferSolanaInstructions(from: Keypair, to: PublicKey, amount: number) {
        return SystemProgram.transfer({
            fromPubkey: from.publicKey,
            toPubkey: to,
            lamports: amount,
        });
    }

    async function multipleSolanaTrasfersWithJito(
        connection: Connection,
        feePayer: Keypair,
        froms: Keypair[],
        tos: PublicKey[],
        amounts: number[],
        jitoTipLamports: number,
        logMessage?: (message: string, type?: 'success' | 'error') => void,
    ) {
        //remove all wallets with 0 amount to send
        const walletsToSend = froms.filter((from, index) => amounts[index] > 0);
        const walletsToReceive = tos.filter((to, index) => amounts[index] > 0);
        const amountsToSend = amounts.filter((amount, index) => amount > 0);

        //remove elements if receiver and sender are the same
        for (let i = 0; i < walletsToSend.length; i++) {
            if (walletsToSend[i].publicKey.equals(walletsToReceive[i])) {
                walletsToSend.splice(i, 1);
                walletsToReceive.splice(i, 1);
                amountsToSend.splice(i, 1);
                i--;
            }
        }
        if (walletsToSend.length === 0) return [];

        const instructions = walletsToReceive.map((to, index) => getTransferSolanaInstructions(walletsToSend[index], to, amountsToSend[index]));

        const instructionsPerTransaction = 8;
        //const transactionsPerBundle = 4;
        const transactionsPerBundle = 1;

        let transactions: VersionedTransaction[] = [];
        const promises = [];

        const transactionsToConfirm = [];

        for (let i = 0; i < instructions.length; i += instructionsPerTransaction) {
            const { blockhash } = await connection.getLatestBlockhash();
            const message = new TransactionMessage({
                payerKey: feePayer.publicKey,
                recentBlockhash: blockhash,
                instructions: [
                    ComputeBudgetProgram.setComputeUnitLimit({ units: transferSolPriorityLimit }),
                    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: transferSolPriorityFee }),
                    ...instructions.slice(i, i + instructionsPerTransaction),
                ],
            }).compileToV0Message();

            const signers = walletsToSend.slice(i, i + instructionsPerTransaction);
            const transaction = new VersionedTransaction(message);
            transaction.sign(signers);
            transaction.sign([feePayer]);
            transactions.push(transaction);

            if (transactions.length === transactionsPerBundle || i + instructionsPerTransaction >= instructions.length) {
                //promises.push(spamAndConfirmBundle(connection, transactions, feePayer, jitoTipLamports));
                promises.push(connection.sendTransaction(transactions[0], { skipPreflight: true }));
                if (logMessage) logMessage(`tx sent: ${bs58.encode(transactions[0].signatures[0])}`, 'success');
                const signature = bs58.encode(transactions[0].signatures[0]);
                const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
                transactionsToConfirm.push({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: signature });
                transactions = [];
            }
        }

        await Promise.all(promises);

        if (logMessage) logMessage(`All sent`);
        return transactionsToConfirm;
    }

    async function transaferSolana(
        connection: Connection,
        walletTipPayer: Keypair,
        from: Keypair,
        to: PublicKey,
        amount: number,
        jitoTipLamports: number,
        useJito: boolean,
    ) {
        const instruction = getTransferSolanaInstructions(from, to, amount);
        const { blockhash } = await connection.getLatestBlockhash();

        const message = new TransactionMessage({
            payerKey: from.publicKey,
            recentBlockhash: blockhash,
            instructions: useJito
                ? [instruction]
                : [
                      ComputeBudgetProgram.setComputeUnitLimit({ units: transferSolPriorityLimit }),
                      ComputeBudgetProgram.setComputeUnitPrice({ microLamports: transferSolPriorityFee }),
                      instruction,
                  ],
        }).compileToV0Message();
        const transaction = new VersionedTransaction(message);

        transaction.sign([from]);

        if (useJito) {
            await spamAndConfirmBundle(connection, [transaction], walletTipPayer, jitoTipLamports);
        } else {
            await connection.sendTransaction(transaction, { skipPreflight: true });
        }
        const signature = bs58.encode(transaction.signatures[0]);

        return signature;
    }

    async function executeInstructionsRpc(
        connection: Connection,
        instructions: TransactionInstruction[],
        signers: Keypair[],
        feePayer: Keypair,
        priorityFeeLamports: number = 5000,
        confirm: boolean = true,
        logMessage?: (message: string, type?: 'success' | 'error') => void,
    ) {
        function isValidInstructionArray(instructions: TransactionInstruction[], signers: Keypair[], blockhash: string) {
            const message = new TransactionMessage({
                payerKey: signers[0].publicKey,
                recentBlockhash: blockhash,
                instructions: [...instructions, ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamports })],
            }).compileToV0Message();
            const versionedTx = new VersionedTransaction(message);
            versionedTx.sign(signers);

            return versionedTx.serialize().length <= 1232;
        }

        if (instructions.length !== signers.length) throw new Error('Instructions and signers length mismatch');

        console.log('Executing instructions...');
        console.log('Instructions length:', instructions.length);
        console.log('Signers length:', signers.length);

        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

        const versionedTxs: VersionedTransaction[] = [];

        const currentInstructions: TransactionInstruction[] = [];
        const currentSigners: Keypair[] = [feePayer];

        for (let i = 0; i <= instructions.length; i++) {
            if (i !== instructions.length && isValidInstructionArray([...currentInstructions, instructions[i]], [...currentSigners, signers[i]], blockhash)) {
                currentInstructions.push(instructions[i]);
                currentSigners.push(signers[i]);
            } else {
                if (currentInstructions.length !== 0) {
                    const tx = new VersionedTransaction(
                        new TransactionMessage({
                            payerKey: feePayer.publicKey,
                            recentBlockhash: blockhash,
                            instructions: [...currentInstructions, ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamports })],
                        }).compileToV0Message(),
                    );
                    tx.sign(currentSigners);
                    versionedTxs.push(tx);

                    currentInstructions.length = 0;
                    currentSigners.length = 0;
                    currentSigners.push(feePayer);
                }
                if (i !== instructions.length) {
                    i--;
                }
            }
        }

        const txsToConfirm: string[] = [];
        const transactionsPerBundle = 1;
        for (let i = 0; i < versionedTxs.length; i += transactionsPerBundle) {
            const txs = versionedTxs.slice(i, i + transactionsPerBundle);
            console.log('tx length', txs[0].serialize().length);
            console.log('tip payer', signers[i].publicKey.toBase58());
            const txId = await connection.sendTransaction(txs[0], { skipPreflight: true });
            const sig = bs58.encode(txs[0].signatures[0]);
            txsToConfirm.push(sig);

            // if (confirm) {
            //     await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: sig }, 'confirmed');
            // }
            if (logMessage) logMessage(`Tx sent with hash: ${sig}, top transaction signature ${bs58.encode(versionedTxs[0].signatures[0])}`);
        }

        return txsToConfirm;
    }

    return {
        getMultipleSolBalances,
        decodeBase58PrivateKey,
        transaferSolana,
        getTransferSolanaInstructions,
        multipleSolanaTrasfersWithJito,
        executeInstructionsRpc,
    };
};
